import { Button, LoadingIndicator } from '@vrplatform/components';
import {
  useApiQuery,
  useIntercom,
  useTeam,
  useTrpcQuery,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { PaymentMethod } from '@hyperline/react-components';
import {
  Anchor,
  Box,
  type BoxProps,
  Center,
  type MantineSpacing,
  Skeleton,
  Stack,
  type StyleProp,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { HiddenFeatureIndicator } from '@vrplatform/embed/components';

export const STORYBOOK_UPGRADE_SUBSCRIPTION_QUERY_KEY =
  'upgrade-billing-subscription';

type Props = {
  title: string;
  description?: string;
  onSuccess?: () => void;
  containerProps?: BoxProps;
  spacing?: StyleProp<MantineSpacing>;
  showDashboard?: () => void;
};

export const UpgradeSubscriptionPaymentForm = ({
  title,
  description = 'Ready to take the next step? Unlock all premium features and keep your progress with our subscription plan.',
  onSuccess,
  containerProps = {
    maw: 500,
    pt: rem(80),
  },
  spacing = rem(80),
  showDashboard,
}: Props) => {
  const { show } = useIntercom();
  const [loadingRedirect, setLoadingRedirect] = useState(false);

  return (
    <Box w="100%" mx="auto" {...containerProps}>
      {title && (
        <Title order={2} mb="sm" data-testid="payment-form-title">
          {title}
        </Title>
      )}
      <Text c="neutral" mb={spacing}>
        {description}
      </Text>
      {loadingRedirect ? (
        <AwaitingRefresh onSuccess={onSuccess} />
      ) : (
        <PaymentForm setRedirectLoading={setLoadingRedirect} />
      )}
      <Text c="gray" mt={spacing}>
        Do you need a help? Don't hesitate to{' '}
        <Anchor component="button" onClick={show}>
          contact our team
        </Anchor>
        .
      </Text>
      {showDashboard && (
        <HiddenFeatureIndicator permission="vrp-admin" mt="md" p="xs">
          <Button
            onClick={showDashboard}
            sx={{
              width: '100%',
            }}
            variant="primary"
          >
            Show data for current session
          </Button>
        </HiddenFeatureIndicator>
      )}
    </Box>
  );
};

const AwaitingRefresh = ({ onSuccess }: { onSuccess: Props['onSuccess'] }) => {
  const [{ id: teamId }, refetchTeam] = useTeam();

  // The Hyperline customer webhook stores the payment method independently of
  // the subscription status, which can stay `pending` (PRO-20063).
  // TODO(PRO-16515): poll `/me` instead once billing fields are part of the
  // bootstrap contract
  const { data: paymentMethodType } = useApiQuery(
    async (api, signal) => {
      const result = await api.GET('/team', { signal });

      if (result.error) throw result.error;

      return result.data.billingPaymentMethodType;
    },
    {
      queryGroup: 'teams',
      uniqueQueryKey: STORYBOOK_UPGRADE_SUBSCRIPTION_QUERY_KEY,
      storybookQueryKey: STORYBOOK_UPGRADE_SUBSCRIPTION_QUERY_KEY,
      variables: { teamId },
      skip: !teamId,
      poll: {
        isCompleted: (type) => !!type,
      },
    }
  );

  useEffect(() => {
    if (paymentMethodType) {
      refetchTeam();
      onSuccess?.();
    }
  }, [paymentMethodType, refetchTeam, onSuccess]);

  return (
    <Center
      mih={'40dvh'}
      sx={{
        flexDirection: 'column',
      }}
    >
      <LoadingIndicator size="md" type="dots" />
      <Text c="neutral" fw={500} size="md" mt="lg">
        Awaiting Confirmation
      </Text>
      <Text c="gray" size="sm" mt="xs" ta="center">
        We are confirming your payment method. This may take a few seconds.
      </Text>
    </Center>
  );
};

const PaymentForm = ({
  setRedirectLoading,
}: {
  setRedirectLoading: (v: boolean) => void;
}) => {
  const [{ id: teamId }] = useTeam();

  const { loading, data, refetch, error } = useTrpcQuery(
    'getHyperlineComponentToken',
    {
      tenantId: teamId,
    },
    {
      skip: !teamId,
      hideErrorNotification: true,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  if (loading)
    return (
      <Stack>
        <Skeleton height={50} />
        <Skeleton height={50} />
        <Skeleton height={50} />
      </Stack>
    );

  if (error || !data?.token?.token)
    return (
      <Stack pt="xl" align="center">
        <Icon icon="AlertTriangleIcon" size={32} color="red" />

        <Text
          c="neutral"
          ta="center"
          component="p"
          size="md"
          fw={500}
          mt={0}
          mb="xs"
        >
          We failed to load the payment form
        </Text>
        <Text c="gray" ta="center" component="p" size="sm" mt={0} mb={rem(40)}>
          {error?.message ||
            'Please reach out to support if the issue persists.'}
        </Text>

        <Button
          variant="primary"
          sx={{
            width: '100%',
            maxWidth: 300,
          }}
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </Stack>
    );

  return (
    <Box mih="300px">
      <PaymentMethod
        className="payment-method-iframe"
        options={{
          token: data.token.token,
          mode: data.isProduction ? 'production' : 'sandbox',
          locale: 'en',
          appearance: {
            variables: {},
          },
        }}
        onPaymentMethodCreated={() => {
          setRedirectLoading(true);
        }}
        height={'100%'}
        style={{
          overflow: 'hidden',
        }}
      />
    </Box>
  );
};
